import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/UI/Button';
import { Alert } from '@/components/UI/Alert';
import { fieldClass } from '@/components/UI/fieldStyles';
import { cn } from '@/utils/cn';
import { useListAgentsQuery, useListModelsQuery } from '@/redux/internal-services/agentsApi';

// Mirrors api-service app/services/llm/model_routing.py. Resolution (individual agent > AllAgent)
// is owned by the backend; this panel only edits the stored rules.

export const ALL_AGENT = 'AllAgent';

type Mode = 'chat' | 'realtime';
// Generation settings set on a route (temperature / top_p / max_tokens); a blank means "model default".
interface RouteParams { temperature?: number; top_p?: number; max_tokens?: number }
interface Rule { provider?: string; model?: string | null; reasoning_effort?: string | null; params?: RouteParams }
// `all` holds AllAgent's reasoning effort + params (its provider/model are the scalar keys below).
interface Routing { agents?: Record<string, Rule>; all?: { reasoning_effort?: string | null; params?: RouteParams } }

const PARAM_FIELDS: { key: keyof RouteParams; label: string; step: string; min: number; max?: number }[] = [
  { key: 'temperature', label: 'Temperature', step: '0.1', min: 0, max: 2 },
  { key: 'top_p', label: 'Top-p', step: '0.05', min: 0, max: 1 },
  { key: 'max_tokens', label: 'Max tokens', step: '1', min: 1, max: 128000 },
];
type ParamText = Record<keyof RouteParams, string>;
const NO_PARAMS: ParamText = { temperature: '', top_p: '', max_tokens: '' };
const paramsToText = (p?: RouteParams): ParamText => ({
  temperature: p?.temperature != null ? String(p.temperature) : '',
  top_p: p?.top_p != null ? String(p.top_p) : '',
  max_tokens: p?.max_tokens != null ? String(p.max_tokens) : '',
});
const paramsFromText = (t: ParamText): RouteParams => {
  const out: RouteParams = {};
  PARAM_FIELDS.forEach(({ key }) => { if (t[key].trim() !== '' && !Number.isNaN(Number(t[key]))) out[key] = Number(t[key]); });
  return out;
};
const paramsSummary = (p?: RouteParams) => {
  const parts = [
    p?.temperature != null && `temp ${p.temperature}`, p?.top_p != null && `top-p ${p.top_p}`, p?.max_tokens != null && `max ${p.max_tokens}`,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : '—';
};
type Values = Record<string, unknown>;
// `intermediate`: this write is only one step of a longer save (the page then skips the refetch and the toast for it).
type SaveFn = (settingKey: string, value: unknown, opts?: { intermediate?: boolean }) => Promise<void>;

const PROVIDERS: Record<Mode, string[]> = {
  chat: ['openai', 'claude', 'grok', 'gemini', 'agno_openai', 'agno_claude', 'agno_grok', 'agno_gemini'],
  realtime: ['openai', 'grok', 'gemini'],
};

// Provider -> the `provider` tag of the models registry (GET /agents/models).
const MODEL_PROVIDER: Record<string, string> = {
  openai: 'openai', agno_openai: 'openai',
  claude: 'anthropic', agno_claude: 'anthropic',
  grok: 'grok', agno_grok: 'grok',
  gemini: 'gemini', agno_gemini: 'gemini',
};

// AllAgent (the default for every agent without its own rule) lives in the scalar keys:
// chat -> chat_provider/chat_model; realtime -> realtime_provider + a provider-specific model key.
// Each realtime backend reads its own AllAgent model key (realtime_backends/*.py `model_key`).
const REALTIME_MODEL_KEY: Record<string, string> = { grok: 'grok_realtime_model', gemini: 'gemini_realtime_model' };
const allModelKey = (mode: Mode, provider: string) =>
  mode === 'chat' ? 'chat_model' : REALTIME_MODEL_KEY[provider] ?? 'realtime_model';
const allProviderKey = (mode: Mode) => (mode === 'chat' ? 'chat_provider' : 'realtime_provider');
const routingKey = (mode: Mode) => `${mode}_routing`;

const asRouting = (v: unknown): Routing => {
  if (typeof v === 'string') { try { v = JSON.parse(v); } catch { return {}; } }
  return v && typeof v === 'object' ? (v as Routing) : {};
};

interface SectionProps {
  mode: Mode;
  title: string;
  values: Values;
  onSave: SaveFn;
  disabled: boolean;
}

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex min-w-0 flex-1 flex-col gap-1 sm:min-w-[180px]">
    <label className="eyebrow">{label}</label>
    {children}
  </div>
);

const RoutingSection: React.FC<SectionProps & { bare?: boolean }> = ({ mode, title, values, onSave, disabled, bare }) => {
  const agentsQ = useListAgentsQuery({ status: 'active', limit: 200 });
  const modelsQ = useListModelsQuery();

  const routing = useMemo(() => asRouting(values[routingKey(mode)]), [values, mode]);
  const allProvider =
    (values[allProviderKey(mode)] as string | undefined) ||
    (mode === 'chat' ? (values.llm_provider as string | undefined) : undefined) || '';

  const [provider, setProvider] = useState('');
  const [selected, setSelected] = useState<string[]>([]); // [ALL_AGENT] or agent ids
  const [model, setModel] = useState('');
  const [effort, setEffort] = useState('');
  const [paramText, setParamText] = useState<ParamText>(NO_PARAMS);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The Agent picker is a <details>; nothing closed it, so it covered the fields below until the page was clicked away.
  const [agentOpen, setAgentOpen] = useState(false);
  const agentRef = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    if (!agentOpen) return undefined;
    const onDown = (e: MouseEvent) => {
      if (agentRef.current && !agentRef.current.contains(e.target as Node)) setAgentOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setAgentOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [agentOpen]);

  const agents = agentsQ.data?.data ?? [];
  const agentName = (id: string) => agents.find((a) => a.agent_id === id)?.agent_name ?? id;
  const isAll = selected.length === 1 && selected[0] === ALL_AGENT;

  const providerModels = useMemo(() => {
    if (!provider) return [];
    const tag = MODEL_PROVIDER[provider] ?? provider;
    return (modelsQ.data ?? []).filter((m) => m.type === mode && (m.provider ?? 'openai') === tag);
  }, [modelsQ.data, provider, mode]);

  // The reasoning efforts the picked model accepts (from its llm_models document); [] = it has none.
  const modelEfforts = (id: string) => providerModels.find((m) => m.model_id === id)?.reasoning?.efforts ?? [];
  const efforts = modelEfforts(model);
  // Which generation settings the picked model accepts (llm_models.params). Realtime models list none.
  const modelParams = (id: string) => providerModels.find((m) => m.model_id === id)?.params ?? {};
  const supported = modelParams(model);
  const hasParams = PARAM_FIELDS.some(({ key }) => supported[key]);

  const savedRule = (id: string): Rule | undefined => {
    if (id === ALL_AGENT) {
      if (!allProvider) return undefined;
      return {
        provider: allProvider,
        model: (values[allModelKey(mode, allProvider)] as string | undefined) ?? null,
        reasoning_effort: routing.all?.reasoning_effort ?? null,
        params: routing.all?.params,
      };
    }
    return routing.agents?.[id];
  };

  // Provider change: drop the agent / model selection that no longer applies.
  const onProvider = (p: string) => { setProvider(p); setSelected([]); setModel(''); setEffort(''); setParamText(NO_PARAMS); setError(null); };

  // A model change keeps the effort only when the new model accepts it.
  const onModel = (id: string) => {
    setModel(id);
    if (effort && !modelEfforts(id).includes(effort)) setEffort('');
    // Drop any setting the new model does not take, keep the rest.
    const takes = modelParams(id);
    setParamText((t) => ({
      temperature: takes.temperature ? t.temperature : '',
      top_p: takes.top_p ? t.top_p : '',
      max_tokens: takes.max_tokens ? t.max_tokens : '',
    }));
  };

  // AllAgent and specific agents are mutually exclusive within one selection.
  const onSelected = (next: string[]) => {
    const cleaned = next.includes(ALL_AGENT) && !selected.includes(ALL_AGENT)
      ? [ALL_AGENT]
      : next.filter((id) => id !== ALL_AGENT);
    setSelected(cleaned);
    setError(null);
    const r = cleaned.length === 1 ? savedRule(cleaned[0]) : undefined;
    const same = !!r && r.provider === provider;
    const nextModel = same ? (r?.model ?? '') : '';
    const savedEffort = same ? (r?.reasoning_effort ?? '') : '';
    setModel(nextModel);
    // A stale value (the registry changed, or the model left it) would be invisible in the disabled select yet still
    // sent on Save, so it is dropped here; saving the rule then clears it.
    setEffort(savedEffort && modelEfforts(nextModel).includes(savedEffort) ? savedEffort : '');
    setParamText(same ? paramsToText(r?.params) : NO_PARAMS);
  };
  const toggle = (id: string) => {
    onSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
    // AllAgent is exclusive (a single choice), so the list is done; individual agents can be ticked several at a time.
    if (id === ALL_AGENT) setAgentOpen(false);
  };

  const current = selected.length === 1 ? savedRule(selected[0]) : undefined;
  const otherProvider = current?.provider && provider && current.provider !== provider ? current.provider : null;
  const modelMissing = !!model && !providerModels.some((m) => m.model_id === model);
  const canSave = !disabled && !!provider && selected.length > 0 && !!model && !saving;
  // Only what the model accepts is ever saved (the server rejects the rest as well).
  const sendEffort = efforts.includes(effort) ? effort : '';
  const params = (() => {
    const all = paramsFromText(paramText);
    const out: RouteParams = {};
    PARAM_FIELDS.forEach(({ key }) => { if (supported[key] && all[key] != null) out[key] = all[key]; });
    return out;
  })();

  // The range a setting may take for the picked model (its own limits where the registry states them).
  const rangeOf = (key: keyof RouteParams) => {
    const f = PARAM_FIELDS.find((p) => p.key === key)!;
    if (key === 'max_tokens') return { lo: supported.max_tokens_min ?? f.min, hi: supported.max_tokens_max ?? f.max };
    if (key === 'temperature') return { lo: f.min, hi: supported.temperature_max ?? f.max };
    return { lo: f.min, hi: f.max };
  };

  const save = async () => {
    if (!canSave) return;
    const bad = PARAM_FIELDS.find(({ key }) => {
      const v = params[key];
      if (v == null) return false;
      if (key === 'max_tokens' && !Number.isInteger(v)) return true;
      const { lo, hi } = rangeOf(key);
      return v < lo || (hi != null && v > hi);
    });
    if (!bad && supported.sampling_exclusive && params.temperature != null && params.top_p != null) {
      setError(`${model} takes temperature or top-p, not both. Leave one empty.`);
      return;
    }
    if (bad) {
      const { lo, hi } = rangeOf(bad.key);
      const whole = bad.key === 'max_tokens' && !Number.isInteger(params[bad.key]) ? 'a whole number ' : '';
      setError(`${bad.label} must be ${whole}between ${lo} and ${hi ?? '∞'} for ${model}.`);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (isAll) {
        // Only touch the routing key when AllAgent has (or had) an effort / settings - leaves untouched configs alone.
        const hadAll = routing.all?.reasoning_effort || Object.keys(routing.all?.params ?? {}).length > 0;
        const hasParams = Object.keys(params).length > 0;
        const writesRouting = !!(sendEffort || hasParams || hadAll);
        // Provider and model first: the server checks the effort against the model as saved. Neither needs fresh values
        // for the next write, so they are marked intermediate; the LAST write of the save is the one that refreshes.
        await onSave(allProviderKey(mode), provider, { intermediate: true });
        await onSave(allModelKey(mode, provider), model, { intermediate: writesRouting });
        if (writesRouting) {
          const rest = { ...routing };
          delete rest.all;                               // nothing left to store -> remove `all` instead of leaving a null
          try {
            await onSave(routingKey(mode), (sendEffort || hasParams)
              ? { ...rest, all: { reasoning_effort: sendEffort || null, ...(hasParams ? { params } : {}) } }
              : rest);
          } catch (e) {
            // Three separate writes: the first two have already gone through, so say so.
            const why = e instanceof Error ? e.message : 'the server refused it';
            throw new Error(`Provider and model were saved, but the reasoning effort / settings were not: ${why}`);
          }
        }
      } else {
        // Only the selected agents change; other agents' rules and AllAgent are untouched.
        const next = { ...(routing.agents ?? {}) };
        selected.forEach((id) => {
          next[id] = {
            provider, model,
            ...(sendEffort ? { reasoning_effort: sendEffort } : {}),
            ...(Object.keys(params).length ? { params } : {}),
          };
        });
        await onSave(routingKey(mode), { ...routing, agents: next });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const removeAgent = (id: string) => {
    const left = { ...(routing.agents ?? {}) };
    delete left[id];
    return onSave(routingKey(mode), { ...routing, agents: left });
  };

  const summary = selected.length === 0 ? 'Select agent…'
    : isAll ? ALL_AGENT
    : selected.length === 1 ? agentName(selected[0]) : `${selected.length} agents`;

  return (
    <div className={bare ? 'space-y-4 px-5 pb-5 pt-2' : 'card-surface space-y-4 p-5'}>
      <div>
        {!bare && <h3 className="text-h3 text-ui-text">{title}</h3>}
        <p className="mt-0.5 text-caption text-ui-text3">
          A specific agent uses its own rule; every other agent uses AllAgent.
        </p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <Field label="Provider">
          <select className={fieldClass({ size: 'sm' })} value={provider} disabled={disabled} onChange={(e) => onProvider(e.target.value)}>
            <option value="">Select provider…</option>
            {PROVIDERS[mode].map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </Field>
        <Field label="Agent">
          <details
            ref={agentRef}
            className="relative"
            open={agentOpen}
            onToggle={(e) => setAgentOpen((e.currentTarget as HTMLDetailsElement).open)}
          >
            <summary
              className={cn(
                fieldClass({ size: 'sm' }), 'flex cursor-pointer list-none items-center',
                (disabled || !provider) && 'pointer-events-none opacity-60',
              )}
            >
              {agentsQ.isLoading ? 'Loading…' : summary}
            </summary>
            <div className="mt-1 max-h-64 w-full overflow-y-auto overscroll-contain rounded-control border border-ui-border bg-ui-card p-1 shadow-pop">
              <label className="flex cursor-pointer items-center gap-2 rounded-control px-2 py-1.5 text-sm hover:bg-ui-hover">
                <input type="checkbox" checked={isAll} onChange={() => toggle(ALL_AGENT)} />
                {ALL_AGENT}
              </label>
              {agents.map((a) => (
                <label key={a.agent_id} className="flex cursor-pointer items-center gap-2 rounded-control px-2 py-1.5 text-sm hover:bg-ui-hover">
                  <input type="checkbox" checked={selected.includes(a.agent_id)} onChange={() => toggle(a.agent_id)} />
                  {a.agent_name}
                </label>
              ))}
            </div>
          </details>
        </Field>
        <Field label="Model">
          <select
            className={cn(fieldClass({ size: 'sm' }), 'font-mono')}
            value={model}
            disabled={disabled || !provider || selected.length === 0 || modelsQ.isLoading}
            onChange={(e) => onModel(e.target.value)}
          >
            <option value="">{modelsQ.isLoading ? 'Loading…' : 'Select model…'}</option>
            {modelMissing && <option value={model}>{model} (not in registry)</option>}
            {providerModels.map((m) => <option key={m.model_id} value={m.model_id}>{m.name}</option>)}
          </select>
        </Field>
        <Field label="Reasoning effort">
          <select
            className={fieldClass({ size: 'sm' })}
            value={effort}
            disabled={disabled || !model || efforts.length === 0}
            onChange={(e) => setEffort(e.target.value)}
            title={model && efforts.length === 0 ? 'This model has no reasoning effort setting' : undefined}
          >
            <option value="">{model && efforts.length === 0 ? 'Not supported' : 'Model default'}</option>
            {efforts.map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
        </Field>
        <div className="flex items-end">
          <Button type="button" size="sm" onClick={save} disabled={!canSave}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      {model && hasParams && (
        <div className="flex flex-col gap-3 sm:flex-row" data-testid="route-params">
          {PARAM_FIELDS.filter(({ key }) => supported[key]).map(({ key, label, step }) => (
            <Field key={key} label={label}>
              <input
                className={fieldClass({ size: 'sm' })}
                type="number" inputMode="decimal" step={step}
                min={rangeOf(key).lo}
                max={rangeOf(key).hi}
                placeholder="Model default"
                value={paramText[key]}
                disabled={disabled}
                onChange={(e) => setParamText((t) => ({ ...t, [key]: e.target.value }))}
              />
            </Field>
          ))}
        </div>
      )}
      {model && supported.sampling_exclusive && supported.temperature && supported.top_p && (
        <p className="text-caption text-ui-text3">This model takes temperature or top-p, not both.</p>
      )}
      {model && !hasParams && mode === 'chat' && (
        <p className="text-caption text-ui-text3">This model takes no temperature / top-p setting{supported.max_tokens ? '; max tokens is the only one it accepts' : ''}.</p>
      )}

      {provider && !modelsQ.isLoading && providerModels.length === 0 && (
        <Alert tone="warning">No {mode} models are registered for {provider}.</Alert>
      )}
      {otherProvider && (
        <Alert tone="info">
          Currently <span className="font-mono">{otherProvider}</span>
          {current?.model ? <> / <span className="font-mono">{current.model}</span></> : null}. Saving replaces it.
        </Alert>
      )}
      {(agentsQ.isError || modelsQ.isError) && <Alert tone="danger">Could not load agents or models.</Alert>}
      {error && <Alert tone="danger">{error}</Alert>}

      <div className="no-scrollbar overflow-x-auto">
        <table className="w-full min-w-[480px] text-sm">
          <thead>
            <tr><th className="th-cell">Agent</th><th className="th-cell">Provider</th><th className="th-cell">Model</th><th className="th-cell">Reasoning</th><th className="th-cell">Settings</th><th className="th-cell" /></tr>
          </thead>
          <tbody>
            <tr className="tr-hover">
              <td className="td-cell">{ALL_AGENT} <span className="text-caption text-ui-text3">· agents without their own rule</span></td>
              <td className="td-cell font-mono text-xs">{allProvider || '—'}</td>
              <td className="td-cell font-mono text-xs">{savedRule(ALL_AGENT)?.model ?? 'agent default'}</td>
              <td className="td-cell font-mono text-xs">{routing.all?.reasoning_effort ?? '—'}</td>
              <td className="td-cell font-mono text-xs">{paramsSummary(routing.all?.params)}</td>
              <td className="td-cell" />
            </tr>
            {Object.entries(routing.agents ?? {}).map(([id, r]) => (
              <tr key={id} className="tr-hover">
                <td className="td-cell">{agentName(id)}</td>
                <td className="td-cell font-mono text-xs">{r.provider}</td>
                <td className="td-cell font-mono text-xs">{r.model ?? '—'}</td>
                <td className="td-cell font-mono text-xs">{r.reasoning_effort ?? '—'}</td>
                <td className="td-cell font-mono text-xs">{paramsSummary(r.params)}</td>
                <td className="td-cell text-right">
                  <Button type="button" variant="destructive-ghost" size="xs" disabled={disabled} onClick={() => removeAgent(id)}>Remove</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/** Catalogue-style line items for the Configurations list: folded by default, each opens one Provider -> Agent -> Model section. */
export const ROUTING_CARDS: { id: 'chat_configuration' | 'realtime_configuration'; mode: 'chat' | 'realtime'; title: string; description: string }[] = [
  { id: 'chat_configuration', mode: 'chat', title: 'Chat Configuration', description: 'Provider and model for text chat, per agent (AllAgent is the default)' },
  { id: 'realtime_configuration', mode: 'realtime', title: 'Realtime Configuration', description: 'Provider and model for live voice sessions, per agent (AllAgent is the default)' },
];

export const RoutingCatalogueCard: React.FC<{
  card: (typeof ROUTING_CARDS)[number];
  values: Values;
  onSave: SaveFn;
  layerLabel?: string;
  disabled?: boolean;
  disabledHint?: string;
  /** Open on load (e.g. the search matched this card). */
  autoExpand?: boolean;
}> = ({ card, values, onSave, layerLabel = 'System', disabled = false, disabledHint, autoExpand }) => {
  const [expanded, setExpanded] = useState(false);
  React.useEffect(() => { if (autoExpand) setExpanded(true); }, [autoExpand]);
  return (
    <div className="table-card">
      <button
        type="button"
        onClick={() => setExpanded((x) => !x)}
        aria-expanded={expanded}
        className="flex w-full items-center justify-between px-5 py-3.5 text-left transition-colors hover:bg-ui-hover focus-ring"
      >
        <div className="flex min-w-0 items-center gap-3">
          <div className="h-2 w-2 shrink-0 rounded-full bg-success" />
          <div className="min-w-0">
            <p className="truncate font-mono text-sm font-semibold text-ui-text">{card.id}</p>
            <p className="mt-0.5 max-w-lg truncate text-xs text-ui-text3">{card.description}</p>
          </div>
        </div>
        <svg className={cn('h-4 w-4 shrink-0 text-ui-text3 transition-transform', expanded && 'rotate-180')} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {expanded && (
        <div className="border-t border-ui-border">
          <div className="px-5 pt-3">
            {disabled ? (
              <Alert tone="info">{disabledHint ?? 'Select a scope to edit.'}</Alert>
            ) : (
              <Alert tone="info">Editing the <strong>{layerLabel}</strong> layer. Rules saved here override the layers below; a layer holds its own full rule set.</Alert>
            )}
          </div>
          <RoutingSection mode={card.mode} title={card.title} values={values} onSave={onSave} disabled={disabled} bare />
        </div>
      )}
    </div>
  );
};

/** Chat and Realtime configuration — independent sections, each Provider -> Agent -> Model. */
export const ModelRoutingPanel: React.FC<{
  values: Values;
  onSave: SaveFn;
  /** Where edits land, e.g. "System" / "Tenant" / "Role" / "User". */
  layerLabel?: string;
  /** Read-only until the layer's scope (tenant / role / user) is picked. */
  disabled?: boolean;
  disabledHint?: string;
}> = ({ values, onSave, layerLabel = 'System', disabled = false, disabledHint }) => (
  <div className="mb-4 space-y-3">
    {disabled ? (
      <Alert tone="info">{disabledHint ?? 'Select a scope to edit.'}</Alert>
    ) : (
      <Alert tone="info">Editing the <strong>{layerLabel}</strong> layer. Rules saved here override the layers below; a layer holds its own full rule set.</Alert>
    )}
    <RoutingSection mode="chat" title="Chat Configuration" values={values} onSave={onSave} disabled={disabled} />
    <RoutingSection mode="realtime" title="Realtime Configuration" values={values} onSave={onSave} disabled={disabled} />
  </div>
);

export const ROUTING_MANAGED_KEYS = [
  'chat_provider', 'chat_model', 'chat_routing',
  'realtime_provider', 'realtime_model', 'grok_realtime_model', 'gemini_realtime_model', 'realtime_routing',
];
